import { useState } from 'react';
import dayjs from 'dayjs';
import { SearchClient } from 'algoliasearch/lite';
import {
  Configure,
  connectInfiniteHits,
  connectRange,
  connectSearchBox,
  InstantSearch,
  Stats
} from 'react-instantsearch-dom';
import SearchBox from './SearchBox';
import RangeSlider from './RangeSlider';
import Hits, { IHitsProps } from './Hits';
import { SearchAction } from './Models';
import { P2GBox, P2GGrid, P2GLink, P2GText } from '@parcel2go/core-ui';
import CustomRefinementList from './CustomRefinedList';
import { TranslationNamespaces } from '~/lib/constants';
import { useTranslation } from 'react-i18next';

const CustomSearchBox = connectSearchBox(SearchBox);
const CustomRangeSlider = connectRange(RangeSlider);
const CustomHits = connectInfiniteHits<IHitsProps, object>(Hits);

export interface ISearchProps {
  client: SearchClient;
  indexName: string;
  analtyicsTags?: string[];
  hit?: SearchAction;
  hideFilter?: boolean;
  hideSearch?: boolean;
}

const SearchPage: React.FC<ISearchProps> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const [showFilter, setShowFilter] = useState(false);
  return (
    <InstantSearch searchClient={props.client} indexName={props.indexName}>
      <Configure
        analytics={true}
        clickAnalytics
        analyticsTags={props.analtyicsTags || ['ACCOUNT_HEADER']}
        hitsPerPage={10}
      />
      <P2GGrid container direction="column" spacing={2}>
        {!props.hideSearch && (
          <P2GGrid item xs={12}>
            <CustomSearchBox />
          </P2GGrid>
        )}
        {!props.hideFilter && (
          <P2GGrid item>
            <P2GGrid container alignItems="center" spacing={2}>
              <P2GGrid item>
                <P2GText>
                  <strong>
                    <Stats />
                  </strong>
                </P2GText>
              </P2GGrid>
              <P2GGrid item>
                <P2GLink onClick={() => setShowFilter(!showFilter)}>
                  {showFilter ? (
                    <>{t('SEARCH_PAGE_REFINE_RESULTS_FILTER_HIDE', { defaultValue: 'Hide' })}</>
                  ) : (
                    <>{t('SEARCH_PAGE_REFINE_RESULTS_FILTER_SHOW', { defaultValue: 'Refine Results' })}</>
                  )}
                </P2GLink>
              </P2GGrid>
            </P2GGrid>
          </P2GGrid>
        )}
        <P2GGrid item>
          {!props.hideFilter && (
            <div style={{ display: showFilter ? 'block' : 'none' }}>
              <P2GBox p={2} bordered mb={2}>
                <P2GGrid container spacing={2}>
                  <P2GGrid item xs={12}>
                    <P2GText variant="h6">
                      {t('SEARCH_PAGE_REFINE_RESULTS_DATE_RANGE', { defaultValue: 'Date Range' })}:{' '}
                    </P2GText>
                    <CustomRangeSlider
                      canRefine
                      title={t('SEARCH_PAGE_REFINE_RESULTS_DATE_RANGE', { defaultValue: 'Date Range' })}
                      attribute="paidDate"
                      min={dayjs().subtract(10, 'year').day(1).unix()}
                      max={dayjs().add(1, 'day').unix()}
                      defaultRefinement={{
                        min: dayjs().subtract(1, 'year').day(1).unix(),
                        max: dayjs().add(1, 'day').unix()
                      }}
                    />
                  </P2GGrid>
                  <P2GGrid item xs={6}>
                    <CustomRefinementList attribute="smartSend.sources" limit={5} searchable showMore>
                      {t('SEARCH_PAGE_REFINE_RESULTS_SOURCE', { defaultValue: 'Source' })}
                    </CustomRefinementList>
                  </P2GGrid>
                  <P2GGrid item xs={6}>
                    <CustomRefinementList attribute="deliveryAddress.countryIso3" limit={5} searchable showMore>
                      {t('SEARCH_PAGE_REFINE_RESULTS_DELIVERY_COUNTRY', { defaultValue: 'Delivery Country' })}
                    </CustomRefinementList>
                  </P2GGrid>
                  <P2GGrid item xs={6}>
                    <CustomRefinementList attribute="service.name" limit={5} searchable showMore>
                      {t('SEARCH_PAGE_REFINE_RESULTS_SERVICE', { defaultValue: 'Service' })}
                    </CustomRefinementList>
                  </P2GGrid>
                  <P2GGrid item xs={6}>
                    <CustomRefinementList attribute="trackingStatus" limit={5} searchable showMore>
                      {t('SEARCH_PAGE_REFINE_RESULTS_TRACKING_STATUS', { defaultValue: 'Tracking Status' })}
                    </CustomRefinementList>
                  </P2GGrid>
                </P2GGrid>
              </P2GBox>
            </div>
          )}
          <P2GBox>
            <CustomHits hit={props.hit} hideFilter={props.hideFilter} />
          </P2GBox>
        </P2GGrid>
      </P2GGrid>
    </InstantSearch>
  );
};

export default SearchPage;
